import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Attribute, Group, User } from 'src/interfaces/user';
import { companyDetails } from 'src/interfaces/company';
import { Individual, IndividualType } from 'src/interfaces/individual';
import { UserService } from 'src/core/services/user.service';
import { AlertService } from 'src/core/services/alert.service';
import { CompanyService } from 'src/core/services/company.service';
import { IndividualService } from 'src/core/services/individual.service';
import { SharedService } from 'src/core/services/shared.service';
import { DirtinessService } from 'src/core/services/dirtiness.service';
import { Observable, of, from } from 'rxjs';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { MatChipInputEvent } from '@angular/material/chips';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { map, startWith, debounceTime, switchMap, catchError } from 'rxjs/operators';
import { PermissionsService } from 'src/core/services/permissions.service';

@Component({
  selector: 'app-user-view',
  templateUrl: './user-view.component.html',
  styleUrls: ['./user-view.component.scss']
})
export class UserViewComponent implements OnInit {

  updateUserForm = this._formBuilder.group({
    email: [{value:'', disabled: true}, Validators.compose([Validators.required, Validators.email])],
    name: ['', Validators.required],
    phone_number: [{value:'', disabled: true}, Validators.pattern(/^\+[0-9]{11,12}$/)],
    roles: [''],
    companySearch: [''],
    individualSearch: [''],
  });

  separatorKeysCodes: number[] = [ENTER, COMMA];
  roleCtrl = this._formBuilder.control('');
  filteredRoles: Observable<string[]>;
  /** Options from API search; emitted on each search term change. */
  filteredCompanies: Observable<companyDetails[]>;
  /** Options from API search for individuals. */
  filteredIndividuals: Observable<Individual[]>;

  roles: string[] = [];
  /** Display name(s) for linked entity (one slot: company name or individual name). */
  companies: string[] = [];
  /** Company id when user is linked to a company. */
  selectedCompanyId: number | null = null;
  /** Individual id when user is linked to an individual. */
  selectedIndividualId: number | null = null;
  /** Whether the linked entity is a company or an individual. */
  linkType: 'company' | 'individual' = 'company';
  /** For individual search list: resolve type label from individual_type_id. */
  individualTypes: IndividualType[] = [];

  allRoles: string[] = [
    'SuperAdmin','OperationsManager','PrescriptionManager', 'Fulfilment', 'Packaging', 'Prescription','Customer','Viewer'
  ];

  selectable = true;
  removable = true;

  @ViewChild('roleInput') roleInput: ElementRef<HTMLInputElement>;
  @ViewChild('companyInput') companyInput: ElementRef<HTMLInputElement>;
  @ViewChild('individualInput') individualInput: ElementRef<HTMLInputElement>;

  user: User | null = null;
  editMode: boolean = false;
  /** Prevents programmatic form updates from marking the page dirty. */
  private isProgrammaticFormUpdate = false;

  constructor(
    private _formBuilder: FormBuilder,
    private _route: ActivatedRoute,
    private _userService: UserService,
    private _companyService: CompanyService,
    private _individualService: IndividualService,
    private _sharedService: SharedService,
    private _alertService: AlertService,
    private _dirtinessService: DirtinessService,
    private _router: Router,
    public readonly perm: PermissionsService
  ) {}

  ngOnInit(): void {

    this
      ._route
      .data
      .subscribe(data => {
        this.user = data.user;
        this.updateView(this.user);
      });
    
      this.updateUserForm
      .valueChanges
      .subscribe(value => {
        if (this.isProgrammaticFormUpdate) return;
        if (!this.updateUserForm.dirty) return;
        this._dirtinessService.setDirty(true);
      })

    this.roles = this.currentRoles;

    (async () => {
      const data = await this._sharedService.getFormData(['individual_type']);
      this.individualTypes = data?.individual_type ?? [];
    })();

    // Company search: only when company mode; driven by companySearch control
    this.filteredCompanies = this.updateUserForm.controls['companySearch'].valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      switchMap((searchText: string) =>
        from(
          this._companyService.getCompanies(1, 50, searchText?.trim() ?? '', null, 'asc', 'name')
        ).pipe(
          map((list) => list.map((c) => ({ id: c.id, name: c.name }))),
          catchError(() => of([]))
        )
      )
    );

    // Individual search: only when individual mode; driven by individualSearch control
    this.filteredIndividuals = this.updateUserForm.controls['individualSearch'].valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      switchMap((searchText: string) =>
        from(
          this._individualService.getIndividuals(1, 50, null, searchText?.trim() ?? null, null, 'asc', 'first_name')
        ).pipe(catchError(() => of([])))
      )
    );

    this.filteredRoles = this.updateUserForm.controls['roles'].valueChanges.pipe(
      startWith(''),
      map((role: string) => this._filterRoles(role)));

  }

  private _filterRoles(value: string): string[] {
    const filterValue = value.toLowerCase();

    return this.allRoles.filter(role => role.toLowerCase().includes(filterValue));
  }

  /** Label for individual type in search list (title + name + type). */
  getIndividualTypeLabel(ind: Individual): string {
    if (!ind?.individual_type_id) return '-';
    const t = this.individualTypes.find((type) => type.id === ind.individual_type_id);
    return t ? t.type : '-';
  }

  getAttribute(attributeName: string, user: User | null) {
    if(!user) return "";
    const attribute: Attribute | undefined
      = user.Attributes.find(attr => attr.Name === attributeName);

    if (!attribute) return "";

    return attribute.Value;
  }

  get currentRoles(): string[] {
    if(!this.user) return [];
    return this.user.Groups.map(group => group.GroupName);
  }

  updateView(user: User | null) {
    if (!user) return;
    this.runProgrammaticFormUpdate(() => {
      this.updateUserForm.patchValue({
        email: this.getAttribute('email', user),
        name: this.getAttribute('name', user),
        phone_number: this.getAttribute('phone_number', user),
        role: user.Groups.length > 0 ? user.Groups[0].GroupName : '',
      });
    });

    // Load user's linked entity (company or individual) for view and edit
    this.loadUserLinkedEntity(user);
    this.updateUserForm.markAsPristine();
    this.updateUserForm.markAsUntouched();
    this._dirtinessService.setDirty(false);
  }

  /**
   * Resolves company id from user (API company_id or custom attribute).
   */
  private getUserCompanyId(user: User | null): number | null {
    if (!user) return null;
    if (user.company_id != null && typeof user.company_id === 'number') return user.company_id;
    const fromAttr = this.getAttribute('custom:company_id', user) || this.getAttribute('company_id', user);
    if (fromAttr != null && fromAttr !== '') {
      const n = parseInt(fromAttr, 10);
      return Number.isNaN(n) ? null : n;
    }
    return null;
  }

  /** Resolves individual id from user (API individual_id or custom attribute). */
  private getUserIndividualId(user: User | null): number | null {
    if (!user) return null;
    if (user.individual_id != null && typeof user.individual_id === 'number') return user.individual_id;
    const fromAttr = this.getAttribute('custom:individual_id', user) || this.getAttribute('individual_id', user);
    if (fromAttr != null && fromAttr !== '') {
      const n = parseInt(fromAttr, 10);
      return Number.isNaN(n) ? null : n;
    }
    return null;
  }

  /** Loads the user's linked company or individual. Shows company when API has company data (so company set by user is visible). When both ids exist with no expanded data, try company first so company sticks. */
  private async loadUserLinkedEntity(user: User | null): Promise<void> {
    try {
      this.companies = [];
      this.selectedCompanyId = null;
      this.selectedIndividualId = null;
      if (!user) return;

      const hasIndividualData = user.individual?.id != null && user.individual?.first_name != null;
      const individualId = this.getUserIndividualId(user);
      const hasCompanyData = user.company?.id != null && user.company?.name != null;
      const companyId = this.getUserCompanyId(user);

      // 1) API returned expanded company and no expanded individual → show company (company was set)
      if (hasCompanyData && !hasIndividualData) {
        this.linkType = 'company';
        this.companies = [user.company!.name];
        this.selectedCompanyId = user.company!.id;
        return;
      }
      // 2) API returned expanded individual → show individual
      if (hasIndividualData) {
        this.linkType = 'individual';
        this.companies = [`${user.individual!.first_name} ${user.individual!.last_name}`.trim()];
        this.selectedIndividualId = user.individual!.id;
        return;
      }
      // 3) Only ids (no expanded): try company first so "company set" shows when backend still has both ids
      if (companyId != null) {
        try {
          const company = await this._companyService.getCompany(companyId);
          if (company?.name != null) {
            this.linkType = 'company';
            this.companies = [company.name];
            this.selectedCompanyId = company.id ?? null;
            return;
          }
        } catch {
          // Ignore
        }
      }
      if (individualId != null) {
        try {
          const individual = await this._individualService.getIndividual(individualId);
          if (individual != null) {
            this.linkType = 'individual';
            this.companies = [`${individual.first_name} ${individual.last_name}`.trim()];
            this.selectedIndividualId = individual.id;
          }
        } catch {
          // Ignore
        }
      }
    } finally {
      // Keep chip search controls in sync without template [disabled] + formControlName (Angular dev warning).
      this.syncLinkSearchControlsDisabled();
    }
  }

  /** Disable company/individual search when a link is already chosen; use FormControl API not [disabled] on bound inputs. */
  private syncLinkSearchControlsDisabled(): void {
    const locked = this.companies.length > 0;
    const companySearch = this.updateUserForm.get('companySearch');
    const individualSearch = this.updateUserForm.get('individualSearch');
    if (locked) {
      companySearch?.disable({ emitEvent: false });
      individualSearch?.disable({ emitEvent: false });
    } else {
      companySearch?.enable({ emitEvent: false });
      individualSearch?.enable({ emitEvent: false });
    }
  }

  /** Toggle edit mode and refresh reactive control disabled state when the form is shown. */
  toggleEditMode(): void {
    this.editMode = !this.editMode;
    if (this.editMode) {
      this.syncLinkSearchControlsDisabled();
    }
  }


  async save() {
    if (!this.user) {
      this._alertService.errorAlert("An error occured could not retrieve user");
      return;
    }
    const success = await this._userService
      .editUser(this.user.Username, {
        email: this.getAttribute('email', this.user),
        name: this.updateUserForm.value.name,
        phone_number: this.getAttribute('phone_number', this.user),
        roles: this.roles,
        company: this.linkType === 'company' ? (this.selectedCompanyId ?? undefined) : null,
        individual_id: this.linkType === 'individual' ? (this.selectedIndividualId ?? undefined) : null
      });

    if (success) {
      this._dirtinessService.setDirty(false);
      const savedLinkType = this.linkType;
      const savedCompanyId = this.selectedCompanyId;
      const savedIndividualId = this.selectedIndividualId;
      const savedCompaniesDisplay = this.companies.length > 0 ? [...this.companies] : [];
      const updated = await this._userService.getUser(this.user.Username);
      if (updated) {
        this.user = updated;
        this.runProgrammaticFormUpdate(() => {
          this.updateUserForm.patchValue({
            email: this.getAttribute('email', updated),
            name: this.getAttribute('name', updated),
            phone_number: this.getAttribute('phone_number', updated),
            role: updated.Groups.length > 0 ? updated.Groups[0].GroupName : '',
          });
        });
        this.roles = updated.Groups.map((g) => g.GroupName);
        await this.loadUserLinkedEntity(updated);
        // If API didn't return company/individual, restore what we just saved so the chip still shows
        if (this.companies.length === 0 && (savedCompanyId != null || savedIndividualId != null)) {
          this.linkType = savedLinkType;
          this.selectedCompanyId = savedCompanyId;
          this.selectedIndividualId = savedIndividualId;
          if (savedCompaniesDisplay.length > 0) {
            this.companies = savedCompaniesDisplay;
          } else if (savedLinkType === 'company' && savedCompanyId != null) {
            try {
              const company = await this._companyService.getCompany(savedCompanyId);
              if (company?.name) this.companies = [company.name];
            } catch {
              this.companies = ['Company'];
            }
          } else if (savedLinkType === 'individual' && savedIndividualId != null) {
            try {
              const individual = await this._individualService.getIndividual(savedIndividualId);
              if (individual) this.companies = [`${individual.first_name} ${individual.last_name}`.trim()];
            } catch {
              this.companies = ['Individual'];
            }
          }
        }
      }
      this.updateUserForm.markAsPristine();
      this.updateUserForm.markAsUntouched();
      this._dirtinessService.setDirty(false);
    }
  }

  /** Runs internal form updates without triggering dirty prompts. */
  private runProgrammaticFormUpdate(updateFn: () => void): void {
    this.isProgrammaticFormUpdate = true;
    try {
      updateFn();
    } finally {
      this.isProgrammaticFormUpdate = false;
    }
  }

  async resetPassword() {
    if(!this.user) return;

    const password = await this._userService.resetPassword(this.user.Username);
    this._alertService.infoAlert(`Password reset to ${password}`);

    // if(success) this.refresh()
  }

  async refresh() {
    if(!this.user) return;
    this.user = await this._userService.getUser(this.user.Username);
    this.updateView(this.user);
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    if (this.roles.indexOf(event.option.viewValue) === -1) {
      this.roles.push(event.option.viewValue);
      this._dirtinessService.setDirty(true);
    }
    this.roleInput.nativeElement.value = '';
    this.updateUserForm.controls['roles'].setValue('');
  }

  add(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();

    // Add our fruit
    if (value) {
      this.roles.push(value);
      this._dirtinessService.setDirty(true);
    }

    // Clear the input value
    event.chipInput!.clear();
  }

  remove(role: string): void {
    const index = this.roles.indexOf(role);

    if (index >= 0) {
      this.roles.splice(index, 1);
      this._dirtinessService.setDirty(true);
    }
  }

  selectedCompany(event: MatAutocompleteSelectedEvent): void {
    const company = event.option.value as companyDetails;
    if (
      company?.id != null &&
      this.companies.indexOf(event.option.viewValue) === -1 &&
      this.companies.length === 0
    ) {
      this.linkType = 'company';
      this.companies.push(event.option.viewValue);
      this.selectedCompanyId = company.id;
      this.selectedIndividualId = null;
      this._dirtinessService.setDirty(true);
    }
    this.updateUserForm.controls['companySearch'].setValue('');
    if (this.companyInput?.nativeElement) this.companyInput.nativeElement.value = '';
    this.selectable = this.companies.length === 0;
    this.syncLinkSearchControlsDisabled();
  }

  selectedIndividual(event: MatAutocompleteSelectedEvent): void {
    const individual = event.option.value as Individual;
    if (individual?.id == null || this.companies.length > 0) return;
    const displayName = `${individual.first_name} ${individual.last_name}`.trim();
    if (this.companies.indexOf(displayName) === -1) {
      this.linkType = 'individual';
      this.companies = [displayName];
      this.selectedIndividualId = individual.id;
      this.selectedCompanyId = null;
      this._dirtinessService.setDirty(true);
    }
    this.updateUserForm.controls['individualSearch'].setValue('');
    if (this.individualInput?.nativeElement) this.individualInput.nativeElement.value = '';
    this.selectable = this.companies.length === 0;
    this.syncLinkSearchControlsDisabled();
  }

  /** Switch between company and individual; clears current selection and search. */
  setLinkType(type: 'company' | 'individual'): void {
    if (this.linkType === type) return;
    this.linkType = type;
    this.companies = [];
    this.selectedCompanyId = null;
    this.selectedIndividualId = null;
    this.updateUserForm.controls['companySearch'].setValue('');
    this.updateUserForm.controls['individualSearch'].setValue('');
    this.selectable = true;
    this.syncLinkSearchControlsDisabled();
    this._dirtinessService.setDirty(true);
  }

  /** Only allow adding via autocomplete selection; reject free text. */
  addCompany(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();
    if (value && this.companies.length === 0) {
      const msg = this.linkType === 'company'
        ? 'Please select a company from the list.'
        : 'Please select an individual from the list.';
      this._alertService.infoAlert(msg);
    }
    event.chipInput!.clear();
    if (this.linkType === 'company') {
      this.updateUserForm.controls['companySearch'].setValue('');
      if (this.companyInput?.nativeElement) this.companyInput.nativeElement.value = '';
    } else {
      this.updateUserForm.controls['individualSearch'].setValue('');
      if (this.individualInput?.nativeElement) this.individualInput.nativeElement.value = '';
    }
    this.selectable = this.companies.length === 0;
    this.syncLinkSearchControlsDisabled();
  }

  removeCompany(company: string): void {
    const index = this.companies.indexOf(company);
    if (index >= 0) {
      this.companies.splice(index, 1);
      this._dirtinessService.setDirty(true);
    }
    if (this.companies.length === 0) {
      this.selectedCompanyId = null;
      this.selectedIndividualId = null;
    }
    this.selectable = this.companies.length === 0;
    this.syncLinkSearchControlsDisabled();
  }


  // Navigate back to user list preserving list filters
  backToList() {
    const m = this._route.snapshot.queryParamMap;
    const q: Record<string, string> = {};
    const search = m.get('search');
    if (search) q.search = search;
    const practice = m.get('search_practice') || m.get('practice');
    if (practice) q.search_practice = practice;
    const enabled = m.get('enabled');
    if (enabled) q.enabled = enabled;
    this._router.navigate(['/portal/users'], { queryParams: q });
  }
}
